/**
 * Created by fers on 26.09.15.
 */

import { assert } from 'chai';
import { singularize } from "inflection";
import {hasPermissionInternal, hasOrganisationPermissionInternal} from '../util/Permission';
import atob from 'atob';
import config from '../config';

export const BACKGROUND_USER: string =  "00000000-0000-0000-0000-000000000000";
export const BACKGROUND_ADMIN: string = "11111111-1111-1111-1111-111111111111";

let entityMap: Record<string, MetaDataHelper> = {};

let isProduction: boolean = config.isProduction;

type FieldType = {
    name: string,
    description: string,
    type: string,
    isPublic: boolean,
    /**
     * Remove spaces from begin/end
     */
    trim: boolean,
    required: boolean,
    defaultValue: any,
    to: Function,
    relativeTo: Array<string>,
    compositeKey: boolean,
    hiddenCreate: boolean,
    parent: boolean,
    userEditable: boolean,
    userCreatable: boolean,
    translatable: boolean,
    values: any,
    filterable: boolean,
    validations: any,
    import: boolean,
    export: boolean,
    controllerFn: string,
    controllerSetFn: string,
    key?: string,
    primaryKey?: boolean,
    permission?: string | string[],
    userUpdatable?: boolean,
};

type RelationType = {
    to: Function,
    from?: MetaDataHelper,
    key?: string,
    shouldFollow?: boolean,
    defaultValue?: any,
};

type MetadataType = {
    entity: string,
    graphQL?: string,
    name: string,
    description?: string,
    isPublic?: boolean,
    fields: Record<string, FieldType> | Function,
    relations: Record<string, RelationType> | Function,
    defaultFilter?: any,
    userEditable?: boolean,
    userUpdatable?: boolean,
    userCreatable?: boolean,
    userDeletable?: boolean,
    optimizeReading?: boolean,
    writePermissionFromParent?: boolean,
    writePermission?: string | string[],
    updatePermission?: string | string[],
    deletePermission?: string | string[],
    createPermission?: string | string[],
    permissionFunc?: (action: string, permissionObject: any, data: any, organisationId: string) => boolean,
    defaultTitle?: (data: any) => string,
};

export const DATE_FORMAT_ISO_8601: string = "iso-8601";

export const DATE_FORMATS: Array<{id: string, label: string}> = [
    {
        id: DATE_FORMAT_ISO_8601,
        label: 'ISO 8601 (2013-02-08 09:30:26.123 or 20130208T080910.123)'
    },
    {
        id: 'DD-MM-YYYY HH:mm Z',
        label: 'DD-MM-YYYY HH:mm Z'
    },
    {
        id: 'MM-DD-YYYY HH:mm Z',
        label: 'MM-DD-YYYY HH:mm Z'
    },
    {
        id: 'X',
        label: 'Unix timestamp (seconds since 1970)'
    },
    {
        id: 'x',
        label: 'Unix ms timestamp (miliseconds since 1970)'
    },
];


/* supported types */
export let supportedTypes: Record<string, {name: string, description?: string, system?: boolean, user?: boolean, isDataSpecific: boolean, maxLength?: number}> = {
    // ...existing code...
    "string": {
        "name": "String",
        system: true,
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "text": {
        "name": "Long text",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "html": {
        "name": "HTML Fragment",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "password": {
        "name": "Password",
        system: true,
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "dateTime": {
        "name": "Date and Time",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "date": {
        "name": "Date",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "binary": {
        "name": "Binary",
        "description": "Binary (Image, PDF, ...)",
        system: true,
        user: false,
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "boolean": {
        "name": "Boolean",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "int": {
        "name": "Integer (NNNN)",
        "description": "Integer number (it is not limited by 32 bits)",
        system: true,
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "currency": {
        "name": "Currency",
        "description": "Currency type in 3 characters form (USD, EUR, CZK)",
        "maxLength": 3,
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "number": {
        "name": "Decimal (NNNN.NN)",
        "description": "Decimal number with decimal point support",
        system: true,
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "object": {
        "name": "Object (JSON)",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "javascript": {
        "name": "JavaScript",
        user: false,
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "expression": {
        "name": "Expression",
        user: false,
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "enum": {
        "name": "Enumeration",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "duration": {
        "name": "Duration",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "email": {
        "name": "Email",
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "time": {
        "name": "Time",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "phoneNumber": {
        "name": "Phone number",
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "vatId": {
        "name": "VAT ID",
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "EAN": {
        "name": "EAN",
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "GPSLocation": {
        "name": "GPS Location",
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "URL": {
        "name": "URL",
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "image": {
        "name": "Image (URL)",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "file": {
        "name": "File (Attachment)",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "js-action": {
        "name": "JavaScript action",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "ZIP": {
        "name": "Postal Code",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "country": {
        "name": "Country",
        "description": "Country in 3 characters form (CZE, DEU, FRA, USA, ...)",
        "maxLength": 3,
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "hostname": {
        "name": "Fully qualified host name",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "hostnameWithoutPort": {
        "name": "Fully qualified host name without port",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "domain": {
        "name": "Domain name",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "IP46": {
        "name": "IPv4/IPv6",
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "IP4": {
        "name": "IPv4",
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "IP6": {
        "name": "IPv6",
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "certificate": {
        "name": "Certificate",
        isDataSpecific: true, // it is specific that type has meaning?
    },
    "stringArray": {
        "name": "Array of Strings",
        user: false,
        system: true,
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "enums": {
        "name": "Enumerations",
        isDataSpecific: false, // it is specific that type has meaning?
    },
    "dictionary": {
        "name": "Dictionary",
        isDataSpecific: false, // it is specific that type has meaning?
    },
};

export let userSupportedTypes: Record<string, {name: string, description?: string, system?: boolean, user?: boolean, isDataSpecific: boolean, maxLength?: number}> = {};
for (let entry of Object.keys(supportedTypes)) {
    if (!('user' in supportedTypes[entry]) || supportedTypes[entry].user === true) {
        userSupportedTypes[entry] = supportedTypes[entry];
    }
}

export class MetaDataHelper {
    FETCH_ERROR: string = '_ERROR';
    FETCH_START: string = '_START';
    FETCH_SUCCESS: string = '_SUCCESS';
    FETCH_LIST: string = 'FETCH_LIST_';
    FETCH_ONE: string = 'FETCH_ONE_';

    private _metadataUnrolled: boolean = false;
    private _metadata: MetadataType;
    private _isEntity: boolean;
    private _primaryKey?: FieldType;
    private _compositeFields?: Array<FieldType>;
    private _parentFieldList?: Array<FieldType>;

    constructor(metadata: MetadataType, isEntity: boolean) {
        if (!isProduction) {
            assert.isDefined(metadata, 'MetaDataHelper.constructor metadata is not defined');
            assert.isDefined(metadata.entity, 'MetaDataHelper.constructor metadata.entity is not defined for: ' + JSON.stringify(metadata));
        }

        this._metadataUnrolled = false;
        this._metadata = metadata;
        this._isEntity = isEntity;
    }

    isEntity(): boolean {
        return this._isEntity;
    }

    /**
     *
     * @returns {string}
     */
    entity(): string {
        /* this must be unrolled */
        return this._metadata.entity;
    }

    singularEntity(): string {
        return singularize(this._metadata.entity);
    }

    /**
     *
     * @returns {string}
     */
    graphQLName(): string {
        /* this must be unrolled */
        return this._metadata.graphQL;
    }

    /**
     *
     * @returns {string}
     */
    name(): string {
        return this._metadata.name;
    }

    pluralName(): string {
        return this._metadata.name;
    }

    description(): string {
        return this._metadata.description;
    }

    isPublic(): boolean {
        return this._metadata.isPublic ? true : false;
    }

    singularName(): string {
        if (this._metadata.name === "Data") {
            return "Data";
        }
        return singularize(this._metadata.name);
    }

    fields(name?: string): Record<string, FieldType> | FieldType {
        if (name) {
            return this.metadata().fields[name];
        } else {
            return this.metadata().fields;
        }
    }

    defaultFilter(): any {
        return this._metadata.defaultFilter;
    }

    relation(name: string): RelationType {
        return this.metadata().relations[name];
    }

    relations(): Record<string, RelationType> {
        return this.metadata().relations;
    }

    metadata(): MetadataType {
        if (this._metadataUnrolled === false) {
            if (!this._metadata['fields']) {
                this._metadata['fields'] = {};
            } else if (typeof this._metadata['fields'] === 'function') {
                this._metadata['fields'] = (this._metadata['fields'] as Function)(entityMap);
            }

            let fields = this._metadata['fields'] as Record<string, FieldType>;
            for (let key of Object.keys(fields)) {
                fields[key]['key'] = key;
            }

            if (!this._metadata['relations']) {
                this._metadata['relations'] = {};
            } else if (typeof this._metadata['relations'] === 'function') {
                this._metadata['relations'] = (this._metadata['relations'] as Function)(entityMap);
            }

            let relations = this._metadata['relations'] as Record<string, RelationType>;
            for (let key of Object.keys(relations)) {
                if (!isProduction) {
                    if (!relations[key]['to']) {
                        throw new Error("Entity " + this.entity() + " has invalid 'to' in relation " + key);
                    }

                    if (relations[key]['to'].entity() === this.entity()) {
                        if (relations[key]['shouldFollow']) {
                            throw new Error("Entity " + this.entity() + " points to itself in relation " + key);
                        }
                    }
                }
                relations[key]['from'] = this;
                relations[key]['key'] = key;
            }

            this._metadataUnrolled = true;
        }
        return this._metadata;
    }

    /**
     * Upper case entity
     *
     * @returns {string}
     */
    upperCaseName(): string {
        return this._metadata.entity.toUpperCase();
    }

    /**
     *
     * @returns {string}
     */
    systemName(): string {
        return this._metadata.entity.replace(" ", "_");
    }


    /**
     * Returns name of primary field.
     * @returns {*}
     */
    getPrimaryKey(): FieldType {
        if (!this._primaryKey) {
            let fields = this.fields() as Record<string, FieldType>;
            for (let key of Object.keys(fields)) {
                let field = fields[key];

                if (field.primaryKey && field.primaryKey === true) {
                    this._primaryKey = field;
                    break;
                }
            }
        }

        return this._primaryKey as FieldType;
    }

    /**
     * Returns default title for object.
     * @returns {*}
     */
    getDefaultTitle(data: any): string {
        if (this._metadata.defaultTitle) {
            return this._metadata.defaultTitle(data);
        }
        return '';
    }


    /**
     * Returns list of composite fields.
     * @returns {*}
     */
    getCompositeKeys(data: any): Array<FieldType> {
        if (!this._compositeFields) {
            this._compositeFields = [];

            let fields = this.fields() as Record<string, FieldType>;
            for (let key of Object.keys(fields)) {
                let field = fields[key];

                if (field.compositeKey && field.compositeKey === true) {
                    this._compositeFields.push(field);
                }
            }

            if (this._compositeFields.length === 0) {
                this._compositeFields.push(this.getPrimaryKey());
            }
        }

        let compositeFields = [...this._compositeFields];

        let parents = this.getAllParentFields();
        /* handle support of multiple parents */
        if (parents.length > 1) {
            for (let parent of parents) {
                if (!data[parent.key + "_id"]) {
                    compositeFields = compositeFields.filter((key) => key !== parent.key);
                }
            }
        }


        return compositeFields;
    }

    /**
     * Returns all parent fields.
     * @returns {*}
     */
    getAllParentFields(): Array<FieldType> {
        if (!this._parentFieldList) {
            let parentFields: Array<FieldType> = [];

            let fields = this.fields() as Record<string, FieldType>;
            for (let key of Object.keys(fields)) {
                let field = fields[key];

                if (field.parent && field.parent === true) {
                    parentFields.push(field);
                }
            }

            this._parentFieldList = parentFields;
        }

        return this._parentFieldList;
    }


    /**
     * Returns name of parent field.
     * @returns {*}
     */
    getParentField(data: any, throwError: boolean = true): FieldType | null {
        let parentFields = this.getAllParentFields();

        if (parentFields.length === 1) {
            return parentFields[0];
        } else if (parentFields.length > 1) {
            assert.isDefined(data);

            let found: FieldType[] = [];

            for (let field of parentFields) {
                if (data[field.key + "_id"]) {
                    found.push(field);
                }
            }

            if (found.length === 1) {
                return found[0];
            } else if (found.length > 1) {
                if (throwError) {
                    assert.isTrue(false, "Multiple parents [" + parentFields.map((field) => field.key).join(", ") + "] in " + this.entity() + " but multi keys data - " + JSON.stringify(data));
                }

                return found[0];
            } else if (throwError) {
                assert.isTrue(false, "Multiple parents [" + parentFields.map((field) => field.key).join(", ") + "] in " + this.entity() + " but missing data - " + JSON.stringify(data));
            }
        }
        return null; // This is to satisfy TypeScript, but in practice this line should never be reached
    }

    /**
     * validate model
     * @param data {object}
     * @returns {boolean}
     */
    validate(data: any): boolean {
        let fields: Record<string, FieldType> = this.fields() as Record<string, FieldType>;

        for (let key of Object.keys(fields)) {
            let f = fields[key];
            if (f.type === "string" || f.type === "text") {

                if (f.translatable) {
                    if (typeof data[f.key] === "object") {
                        continue;
                    }
                }

                if (typeof data[f.key] !== "undefined" && data[f.key] !== null && typeof data[f.key] !== "string") {
                    throw new Error(this.entity() + "." + f.key + " is not of type string (" + (typeof data[f.key]) + ")");
                }

                if (f.required) {
                    if (!data[f.key] || data[f.key].length === 0) {
                        throw new Error(f.name + " is required");
                    }
                }
            } else if (f.type === "boolean") {
                if (typeof data[f.key] !== "undefined" && data[f.key] !== null && typeof data[f.key] !== "boolean") {
                    throw new Error(this.entity() + "." + f.key + " is not of type boolean (" + (typeof data[f.key]) + ")");
                }
            }
        }

        return true;
    }


    /**
     * return default Values
     *
     * @returns {object}
     */
    defaultValues(parent: any): Record<string, any> {
        let defVal: Record<string, any> = {};

        defVal['_type'] = this.entity();

        let fields = this.fields() as Record<string, FieldType>;
        for (let key of Object.keys(fields)) {
            let f = fields[key];

            if ('defaultValue' in f) {
                let val;
                if (typeof f.defaultValue === "function") {
                    val = f.defaultValue(parent);
                } else {
                    val = f.defaultValue;
                }

                if (typeof val === "object") {
                    defVal[key] = JSON.parse(JSON.stringify(val));
                } else if (f.type === "relation") {
                    defVal[key + "_id"] = val;
                } else {
                    defVal[key] = val;
                }
            } else if (f.parent && f.hiddenCreate) {
                let parentType = parent["_type"];
                if (parentType) {
                    if (parentType === f.to().entity()) {
                        defVal[key + "_id"] = parent['id'];
                    }
                } else if (parent && parent['id']) {
                    let ids = atob(parent['id']).split(":");
                    if (ids.length > 0) {
                        parentType = ids[0];

                        if (parentType === f.to().graphQLName()) {
                            defVal[key + "_id"] = parent['id'];
                        }
                    }

                    assert.isDefined(parentType);
                }

            } else if (f.type === "string") {
                defVal[key] = '';
            } else if (f.type === "string") {
                defVal[key] = '';
            } else if (f.type === "boolean") {
                defVal[key] = false;
            }
        }

        let relations = this.relations();
        for (let key of Object.keys(relations)) {
            let r = relations[key];

            if ('defaultValue' in r) {
                let val;
                if (typeof r.defaultValue === "function") {
                    val = r.defaultValue(parent);
                } else {
                    val = r.defaultValue;
                }

                defVal[key] = JSON.parse(JSON.stringify(val));
            }
        }

        return defVal;
    }

    isFieldVisible(permissionObject: any, name: string, data: any): boolean {
        let f = this.fields(name) as FieldType;

        if (f && 'permission' in f) {
            if (!this._canWrite(permissionObject, data ? data.organisation_id : null, f.permission)) {
                return false;
            }
        }

        return true;
    }

    isFieldEditable(name: string, data: any): boolean {
        if (!('userEditable' in this.metadata()) || this.metadata().userEditable) {
            let f = this.fields(name) as FieldType;

            if (!('userEditable' in f) || f.userEditable) {
                return true;
            }
        }

        if (!('userUpdatable' in this.metadata()) || this.metadata().userUpdatable) {
            let f = this.fields(name) as FieldType;

            if (('userEditable' in f) && !f.userEditable) {
                return false;
            }

            if (!('userUpdatable' in f) || f.userUpdatable) {
                return true;
            }
        }


        return false;
    }

    isFieldCreatable(name: string, data: any): boolean {
        if (!('userEditable' in this.metadata()) || this.metadata().userEditable) {
            let f = this.fields(name) as FieldType;

            if (!('userEditable' in f) || f.userEditable) {
                return true;
            }
        }

        if (!('userCreatable' in this.metadata()) || this.metadata().userCreatable) {
            let f = this.fields(name) as FieldType;

            if (('userEditable' in f) && !f.userEditable) {
                return false;
            }

            if (!('userCreatable' in f) || f.userCreatable) {
                return true;
            }
        }

        return false;
    }

    _canWrite(permissionObject: any, organisationId: string | null, list: string | string[] | undefined): boolean {
        if (list) {
            if (typeof list === 'string') {
                list = [list];
            }
            for (let permission of list) {
                if (organisationId) {
                    if (!hasOrganisationPermissionInternal(permission, organisationId, permissionObject)) {
                        return false;
                    }
                } else if (!hasPermissionInternal(permission, permissionObject)) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     *
     * @param data
     * @returns {boolean}
     */
    canUpdate(permissionObject: any, data: any, organisationId: string): boolean {
        let metadata = this.metadata();
        if (this.metadata().writePermissionFromParent) {
            let parent = this.getParentField(data, false);

            metadata = parent.to();
        }

        if (!this._canWrite(permissionObject, organisationId, metadata.writePermission)) {
            return false;
        }
        if (!this._canWrite(permissionObject, organisationId, metadata.updatePermission)) {
            return false;
        }

        if ('permissionFunc' in metadata) {
            return metadata.permissionFunc('update', permissionObject, data, organisationId);
        }

        return true;
    }

    /**
     * Is this entry editable by user (by any user, not this one)
     *
     * @param data
     * @returns {boolean}
     */
    canUserUpdate(data: any): boolean {
        if (!('userEditable' in this.metadata()) || this.metadata().userEditable) {
            return true;
        }

        if (this.metadata().userUpdatable) {
            return true;
        }

        return false;
    }


    /**
     *
     * @param data
     * @returns {boolean}
     */
    canDelete(permissionObject: any, data: any, organisationId: string): boolean {
        let metadata = this.metadata();
        if (this.metadata().writePermissionFromParent) {
            let parent = this.getParentField(data, false);

            metadata = parent.to();
        }

        if (!this._canWrite(permissionObject, organisationId, metadata.writePermission)) {
            return false;
        }
        if (!this._canWrite(permissionObject, organisationId, metadata.deletePermission)) {
            return false;
        }

        if ('permissionFunc' in metadata) {
            return metadata.permissionFunc('delete', permissionObject, data, organisationId);
        }

        return true;
    }

    /**
     *
     * @param data
     * @returns {boolean}
     */
    canUserDelete(data: any): boolean {
        if (!('userEditable' in this.metadata()) || this.metadata().userEditable) {
            return true;
        }

        if (this.metadata().userDeletable) {
            return true;
        }

        return false;
    }

    /**
     *
     * @param data
     * @returns {boolean}
     */
    canView(permissionObject: any, data: any, organisationId: string): boolean {
        return true;
    }

    /**
     *
     * @param data
     * @returns {boolean}
     */
    canCreate(permissionObject: any, data: any, organisationId: string): boolean {
        let metadata = this.metadata();
        if (this.metadata().writePermissionFromParent) {
            let parent = this.getParentField(data, false);

            metadata = parent.to();
        }

        if (metadata.createPermission) {
            if (!this._canWrite(permissionObject, organisationId, metadata.createPermission)) {
                return false;
            }
        } else if (!this._canWrite(permissionObject, organisationId, metadata.writePermission)) {
            return false;
        }

        if ('permissionFunc' in metadata) {
            return metadata.permissionFunc('create', permissionObject, data, organisationId);
        }

        return true;
    }

    /**
     *
     * @param data
     * @returns {boolean}
     */
    canUserCreate(data: any): boolean {
        if (!('userEditable' in this.metadata()) || this.metadata().userEditable) {
            return true;
        }

        if (this.metadata().userCreatable) {
            return true;
        }

        return false;
    }

    /**
     * Translates local relativeTo fields to compositeKeys in parent
     *
     * it can have different name here than in parent (but it is composite key in parent)
     *
     * @param ids
     * @param field
     * @returns {{}}
     */
    translateCompositeKeysForParent(ids: any, field: FieldType): any {
        if (!ids) {
            return;
        }

        assert.isDefined(field, "Field is empty");
        let parentMetadata = field.to();
        let compositeKeys = parentMetadata.getCompositeKeys(ids);
        let newIds: Record<string, any> = {};

        if (field.relativeTo) {
            for (let related of field.relativeTo) {
                let relatedField = this.fields(related) as FieldType;
                let e;

                let fieldInParent;

                if (relatedField.primaryKey) {
                    e = this.entity();
                } else {
                    e = relatedField.to().entity();
                }

                fieldInParent = compositeKeys.find((ck) => ck.type === "relation" && ck.to().entity() === e);
                if (!fieldInParent) {
                    throw new Error("Can't find parent " + e + " as relativeTo " + field.key);
                }

                let value = relatedField.primaryKey ? ids[related] : ids[related + "_id"];

                if (fieldInParent.type === "relation") {
                    newIds[fieldInParent.key + "_id"] = value;
                } else {
                    newIds[fieldInParent.key] = value;
                }
            }
        }

        if (newIds['flowDef_id'] || parentMetadata.entity() === "flowDefs" || this.entity() === "flowDefs") {
            if (newIds['flowDefVersion_id']) {
                /* do nothing */
            } else if (ids['flowDefVersion_id']) {
                newIds['flowDefVersion_id'] = ids['flowDefVersion_id'];
            } else if (ids['flow_id']) {
                newIds['flow_id'] = ids['flow_id'];
            }
        }

        if (newIds['channelDef_id'] || parentMetadata.entity() === "channelDefs" || this.entity() === "channelDefs") {
            if (newIds['channelDefVersion_id']) {
                /* do nothing */
            } else if (ids['channelDefVersion_id']) {
                newIds['channelDefVersion_id'] = ids['channelDefVersion_id'];
            } else if (ids['channel_id']) {
                newIds['channel_id'] = ids['channel_id'];
            }
        }

        if (newIds['libraryDef_id'] || parentMetadata.entity() === "libraryDefs" || this.entity() === "libraryDefs") {
            if (newIds['libraryDefVersion_id']) {
                /* do nothing */
            } else if (ids['libraryDefVersion_id']) {
                newIds['libraryDefVersion_id'] = ids['libraryDefVersion_id'];
            }
        }

        if (newIds['groupDef_id'] || parentMetadata.entity() === "groupDefs" || this.entity() === "groupDefs") {
            if (newIds['groupDefVersion_id']) {
                /* do nothing */
            } else if (ids['groupDefVersion_id']) {
                newIds['groupDefVersion_id'] = ids['groupDefVersion_id'];
            }
        }

        if (newIds['scenarioDef_id'] || parentMetadata.entity() === "scenarioDefs" || this.entity() === "scenarioDefs") {
            if (newIds['scenarioDefVersion_id']) {
                /* do nothing */
            } else if (ids['scenarioDefVersion_id']) {
                newIds['scenarioDefVersion_id'] = ids['scenarioDefVersion_id'];
            }
        }


        let primaryId;
        if (ids[field.key]) {
            primaryId = ids[field.key];
        } else {
            primaryId = ids[field.key + "_id"];
        }
        if (Object.keys(newIds).length === 0) {
            newIds = primaryId;
        } else {
            newIds[parentMetadata.getPrimaryKey().key] = primaryId;

            if (!newIds[parentMetadata.getPrimaryKey().key]) {
                return;
            }
        }

        return newIds;
    }
}


export function declareEntityList(list: Array<MetaDataHelper>): Record<string, MetaDataHelper> {
    for (let l of list) {
        entityMap[l.entity()] = l;
    }

    return entityMap;
}

export function declareEntity(md: MetadataType): MetaDataHelper {
    return new MetaDataHelper(md, true);
}

export function declareLinkedEntity(md: MetadataType): MetaDataHelper {
    return new MetaDataHelper(md, false);
}

/**
 * Entity used only for editing in browser (no server support)
 * @param md
 * @returns {MetaDataHelper}
 */
export function declareEditEntity(md: MetadataType): MetaDataHelper {
    return new MetaDataHelper(md, false);
}

export default MetaDataHelper;